import React, { createContext, useCallback, useContext, useMemo, useRef } from "react";
import { registerOverlayDismissHandler } from "@app/overlay/overlayDismissRegistry";
import { useWindowControls } from "../hooks/useWindowControls";

interface OverlayDismissBridgeContextValue {
    registerDismissHandler: (handler: () => void) => () => void;
    requestDismiss: () => void;
}

const OverlayDismissBridgeContext = createContext<OverlayDismissBridgeContextValue | null>(null);

/**
 * Связывает кнопку закрытия shell с логикой dismiss формы (unsaved guard и т.п.).
 * Размещается внутри window-surface, оборачивает shell + content.
 */
export function OverlayDismissBridgeProvider({ children }: { children: React.ReactNode }) {
    const windowControls = useWindowControls();
    const dismissHandlerRef = useRef<(() => void) | null>(null);

    const registerDismissHandler = useCallback((handler: () => void) => {
        dismissHandlerRef.current = handler;
        return () => {
            if (dismissHandlerRef.current === handler) {
                dismissHandlerRef.current = null;
            }
        };
    }, []);

    const requestDismiss = useCallback(() => {
        if (dismissHandlerRef.current != null) {
            dismissHandlerRef.current();
            return;
        }

        windowControls.close();
    }, [windowControls]);

    React.useEffect(
        () => registerOverlayDismissHandler(windowControls.id, requestDismiss),
        [requestDismiss, windowControls.id],
    );

    const value = useMemo(
        () => ({
            registerDismissHandler,
            requestDismiss,
        }),
        [registerDismissHandler, requestDismiss],
    );

    return <OverlayDismissBridgeContext.Provider value={value}>{children}</OverlayDismissBridgeContext.Provider>;
}

/** Регистрирует обработчик dismiss (обычно из mobile form window host). */
export function useOverlayDismissRegistration(handler: () => void): void {
    const bridge = useContext(OverlayDismissBridgeContext);

    React.useEffect(() => {
        if (bridge == null) {
            return undefined;
        }

        return bridge.registerDismissHandler(handler);
    }, [bridge, handler]);
}

/** Запрос закрытия overlay: сначала зарегистрированный handler, иначе прямой `close()`. */
export function useOverlayDismissRequest(): () => void {
    const bridge = useContext(OverlayDismissBridgeContext);
    const windowControls = useWindowControls();

    return useCallback(() => {
        if (bridge != null) {
            bridge.requestDismiss();
            return;
        }

        windowControls.close();
    }, [bridge, windowControls]);
}
